import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { capturePhoto, getCurrentPosition } from "@/lib/native";
import { StatusPill, Spinner } from "@/components/ui-bits";
import { ArrowLeft, Camera, Play, Pause, Square, CheckCircle2, MapPin, Clock, Users as UsersIcon, Ship, Trash2, CheckSquare, FileText, Save, X, AlertTriangle } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

export default function DetalleTrabajo() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [m, setM] = useState(null);
  const [emb, setEmb] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [servs, setServs] = useState([]);        // servicios asignados al mantenimiento
  // checklist_state se persiste en backend. Se inicializa desde m.checklist_state y longitud emb.checkouts.
  const checklistState = (() => {
    const total = (emb?.checkouts || []).length;
    const saved = m?.checklist_state || [];
    if (saved.length >= total) return saved.slice(0, total);
    return [...saved, ...Array(total - saved.length).fill(false)];
  })();
  const checklistComplete = (emb?.checkouts || []).length > 0 && checklistState.every(Boolean);
  const checklistTotal = (emb?.checkouts || []).length;
  const checklistDone = checklistState.filter(Boolean).length;
  const canFinish = checklistDone >= checklistTotal;
  const finishHint = t("detalle.btn.finish_blocked_checklist", "Para finalizar completa toda la checklist ({cd}/{ct}).", { cd: checklistDone, ct: checklistTotal });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoComment, setPhotoComment] = useState("");
  const [editObs, setEditObs] = useState(false);
  const [obsText, setObsText] = useState("");
  const [savingObs, setSavingObs] = useState(false);
  const [editInc, setEditInc] = useState(false);
  const [incText, setIncText] = useState("");
  const [savingInc, setSavingInc] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/mantenimientos/${id}`);
      setM(data);
      const [e, s, c] = await Promise.all([api.get("/embarcaciones"), api.get("/servicios"), api.get("/clientes").catch(() => null)]);
      const embFound = e.data.find((x) => x.id === data.embarcacion_id);
      setEmb(embFound);
      if (c && embFound) setCliente(c.data.find((x) => x.id === embFound.cliente_id));
      const serviceIds = (data.services || []).map((x) => x.service_id);
      setServs(s.data.filter((x) => serviceIds.includes(x.id)));
      const u = await api.get("/users").catch(() => null);
      if (u) setEmpleados(u.data.filter((x) => (data.empleado_ids || []).includes(x.id)));
    } catch (ex) { setErr(fmtErr(ex)); }
  };
  useEffect(() => { load(); }, [id]);

  const withGeo = () => getCurrentPosition();

  const doCheckin = async () => {
    setBusy(true); setErr("");
    try {
      const g = await withGeo();
      const { data } = await api.post(`/mantenimientos/${id}/checkin`, g);
      setM(data);
      // Al iniciar un mantenimiento, refrescamos el badge de pendientes
      window.dispatchEvent(new CustomEvent("sns:refresh-pending"));
    }
    catch (ex) { setErr(fmtErr(ex)); } finally { setBusy(false); }
  };
  const doCheckout = async () => {
    setBusy(true); setErr("");
    try { const g = await withGeo(); const { data } = await api.post(`/mantenimientos/${id}/checkout`, g); setM(data); }
    catch (ex) { setErr(fmtErr(ex)); } finally { setBusy(false); }
  };
  const doPause = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/mantenimientos/${id}/pause`); setM(data); }
    catch (ex) { setErr(fmtErr(ex)); } finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post(`/mantenimientos/${id}/resume`); setM(data); }
    catch (ex) { setErr(fmtErr(ex)); } finally { setBusy(false); }
  };
  const saveObservaciones = async () => {
    setSavingObs(true); setErr("");
    try {
      const { data } = await api.patch(`/mantenimientos/${id}/observaciones`, { observaciones: obsText });
      setM(data); setEditObs(false);
    } catch (ex) { setErr(fmtErr(ex)); } finally { setSavingObs(false); }
  };
  const saveIncidencias = async () => {
    setSavingInc(true); setErr("");
    try {
      const { data } = await api.patch(`/mantenimientos/${id}/incidencias`, { incidencias: incText });
      setM(data); setEditInc(false);
    } catch (ex) { setErr(fmtErr(ex)); } finally { setSavingInc(false); }
  };

  const toggleServicioRealizado = async (service_id, realizado) => {
    try {
      const { data } = await api.post(`/mantenimientos/${id}/servicio-realizado`, { service_id, realizado });
      setM(data);
    } catch (ex) { setErr(fmtErr(ex)); }
  };

  const toggleChecklist = async (index, value) => {
    try {
      const { data } = await api.post(`/mantenimientos/${id}/checklist`, { index, value });
      setM(data);
    } catch (ex) { setErr(fmtErr(ex)); }
  };

  // Reduce el tamaño de la imagen para evitar subidas de 5+MB y timeouts
  const resizeImage = (dataUrl, maxWidth = 1600, quality = 0.8) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    // Reset input para que volver a seleccionar la misma imagen también dispare el onChange
    if (e.target) e.target.value = "";
    if (!f) return;
    const r = new FileReader();
    r.onload = async () => {
      try {
        const compressed = await resizeImage(r.result);
        setPendingPhoto(compressed);
      } catch {
        setPendingPhoto(r.result);
      }
    };
    r.readAsDataURL(f);
  };
  const takeNativePhoto = async () => {
    const du = await capturePhoto();
    if (du) {
      try { setPendingPhoto(await resizeImage(du)); }
      catch { setPendingPhoto(du); }
    }
  };
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const savePhoto = async () => {
    if (uploadingPhoto || !pendingPhoto) return; // evita doble click
    setUploadingPhoto(true); setErr("");
    try {
      const { data } = await api.post(`/mantenimientos/${id}/photo`, { data_url: pendingPhoto, comment: photoComment });
      setM(data); setPendingPhoto(null); setPhotoComment("");
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setUploadingPhoto(false); }
  };
  const deletePhoto = async (idx) => {
    if (!confirm(t("detalle.photos.confirm_delete"))) return;
    try { const { data } = await api.delete(`/mantenimientos/${id}/photo/${idx}`); setM(data); }
    catch (ex) { setErr(fmtErr(ex)); }
  };

  const remove = async () => {
    if (!confirm(t("detalle.confirm.delete"))) return;
    try { await api.delete(`/mantenimientos/${id}`); nav("/trabajos"); }
    catch (ex) { setErr(fmtErr(ex)); }
  };

  if (!m) return <Spinner label={t("common.loading")} />;
  const isAdmin = user?.role === "admin";
  const servMap = Object.fromEntries(servs.map((s) => [s.id, s]));
  // Estado de check-in de ESTE usuario: en un trabajo con varios empleados, cada uno
  // ficha por su cuenta (worker_sessions), así que el botón no puede depender solo
  // del estado global m.status o el segundo trabajador nunca vería "Iniciar".
  const mySession = (m.worker_sessions || []).find((s) => s.empleado_id === user?.id);
  const myStatus = mySession ? mySession.status
    : (m.status === "finalizado" ? m.status : "programado");

  return (
    <div className="pb-24 sns-fade-up max-w-5xl mx-auto" data-testid="detalle-trabajo">
      <div className="px-5 pt-6 pb-2" data-testid="detalle-header">
        <button
          onClick={() => nav(-1)}
          className="h-12 pl-3 pr-5 rounded-full bg-[var(--sky)] text-[var(--sky-text)] font-bold text-base flex items-center gap-2 active:scale-95 transition-transform"
          data-testid="btn-back"
        >
          <ArrowLeft size={24} strokeWidth={2.5} /> {t("common.back", "Volver")}
        </button>
        <div className="text-center mt-5">
          <p className="text-[var(--coral)] text-xs font-bold uppercase tracking-[0.2em] mb-1" data-testid="detalle-tag">{t("detalle.tag")}</p>
          <h1 className="sns-title text-3xl text-[var(--ink)] break-words" data-testid="detalle-emb-name">{emb?.name || "—"}</h1>
          {emb?.description && <p className="text-slate-500 text-sm mt-1">{emb.description}</p>}
          <div className="flex justify-center mt-3"><StatusPill status={m.status} /></div>
        </div>
      </div>

      <div className="px-5 space-y-4 pt-4">
        <div className="sns-card p-5 space-y-3 text-sm">
          {cliente && isAdmin && (
            <Row icon={<UsersIcon size={16} />} label={t("detalle.client")} value={cliente.empresa || cliente.nombre} />
          )}
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--sky-text)] pt-1 text-center" data-testid="detalle-scheduled-label">{t("detalle.scheduled")}</div>
          <Row icon={<Clock size={16} />} label={t("detalle.start_planned")} value={fmtDT(m.start_at)} />
          {m.end_at && (
            <Row icon={<Clock size={16} />} label={t("detalle.end_planned")} value={fmtDT(m.end_at)} />
          )}
          {emb?.puerto && <Row icon={<MapPin size={16} />} label={t("detalle.location")} value={emb.puerto} />}
          {empleados.length > 0 && (
            <Row icon={<UsersIcon size={16} />} label={t("detalle.employees")} value={empleados.map((u) => u.name).join(", ")} />
          )}
          {emb?.matricula && <Row icon={<Ship size={16} />} label={t("detalle.plate")} value={emb.matricula} />}
        </div>

        {/* Horas reales (visible cuando hay checkin o admin) */}
        {(m.checkin_at || isAdmin) && (
          <HorasRealesCard m={m} isAdmin={isAdmin} onUpdated={(d) => setM(d)} setErr={setErr} t={t} />
        )}

        {/* Observaciones (solo admin edita; empleado solo lee) */}
        {(isAdmin || m.observaciones) && (
          <div className="sns-card p-5" data-testid="observaciones-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="sns-title text-base flex items-center gap-2">
                <FileText size={18} /> {t("detalle.observations")}
              </h3>
              {isAdmin && !editObs && m.status !== "finalizado" && (
                <button
                  onClick={() => { setObsText(m.observaciones || ""); setEditObs(true); }}
                  className="text-sm text-[var(--coral)] font-semibold"
                  data-testid="btn-edit-observaciones"
                >
                  {m.observaciones ? t("detalle.edit") : t("detalle.add")}
                </button>
              )}
            </div>
            {editObs ? (
              <div className="space-y-2">
                <textarea
                  value={obsText}
                  onChange={(e) => setObsText(e.target.value)}
                  rows={4}
                  placeholder={t("detalle.observations.placeholder")}
                  className="sns-input resize-none"
                  data-testid="observaciones-textarea"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setEditObs(false); setObsText(""); }}
                    className="sns-btn sns-btn-ghost bg-slate-100"
                    data-testid="btn-cancel-observaciones"
                  >
                    <X size={16} /> {t("common.cancel")}
                  </button>
                  <button
                    onClick={saveObservaciones}
                    disabled={savingObs}
                    className="sns-btn sns-btn-accent disabled:opacity-60"
                    data-testid="btn-save-observaciones"
                  >
                    <Save size={16} /> {savingObs ? t("common.saving") : t("common.save")}
                  </button>
                </div>
              </div>
            ) : m.observaciones ? (
              <p className="text-slate-700 whitespace-pre-wrap" data-testid="observaciones-text">{m.observaciones}</p>
            ) : (
              <p className="text-slate-400 text-sm italic">{t("detalle.observations.empty")}</p>
            )}
          </div>
        )}

        {/* Servicios con realizado + gestión (añadir/quitar) */}
        <div className="sns-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="sns-title text-base">
              {t("detalle.services")} ({(m.services || []).filter((s) => s.realizado).length}/{m.services?.length || 0})
            </h3>
            <div className="flex gap-3 items-center">
              {isAdmin && (m.services || []).length > 0 && (m.services || []).some((s) => !s.realizado) && (
                <button
                  onClick={async () => {
                    try {
                      // Marca cada servicio no realizado como realizado
                      let last = m;
                      for (const s of (m.services || [])) {
                        if (!s.realizado) {
                          const { data } = await api.post(`/mantenimientos/${id}/servicio-realizado`, { service_id: s.service_id, realizado: true });
                          last = data;
                        }
                      }
                      setM(last);
                    } catch (ex) { setErr(fmtErr(ex)); }
                  }}
                  className="text-xs text-emerald-700 font-semibold"
                  data-testid="btn-mark-all-realizado"
                >
                  {t("detalle.mark_all")}
                </button>
              )}
              {isAdmin && m.status !== "finalizado" && (
                <Link
                  to={`/editar-mantenimiento/${m.id}`}
                  className="text-sm text-[var(--coral)] font-semibold"
                  data-testid="btn-edit-servicios"
                >
                  {t("detalle.edit")}
                </Link>
              )}
            </div>
          </div>
          {/* Aviso: finalizado pero sin servicios marcados como realizados → no facturable */}
          {m.status === "finalizado" && (m.services || []).length > 0 && !(m.services || []).some((s) => s.realizado) && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 mb-3 text-xs" data-testid="warning-no-realizados">
              {t("detalle.warning_not_billable")}
            </div>
          )}
          {(m.services || []).length === 0 ? (
            <p className="text-slate-400 text-sm italic">{t("detalle.no_assigned")}</p>
          ) : (
            <div className="space-y-2">
              {(m.services || []).map((s) => {
                const sv = servMap[s.service_id];
                if (!sv) return null;
                const lockedDone = m.status === "finalizado" && !isAdmin;
                return (
                  <div key={s.service_id} className="flex items-center gap-3 py-2 border-b border-[var(--border)] last:border-0" data-testid={`svc-${s.service_id}`}>
                    <input type="checkbox" checked={!!s.realizado}
                           onChange={() => !lockedDone && toggleServicioRealizado(s.service_id, !s.realizado)}
                           disabled={lockedDone}
                           className="w-5 h-5 accent-[var(--coral)] cursor-pointer" />
                    <span className="sns-pill bg-[var(--sky)] text-[var(--sky-text)] shrink-0">{sv.code || "•"}</span>
                    <span className="flex-1 font-medium">{sv.name}</span>
                    <span className="text-sm text-slate-500 tabular-nums">{sv.price ? `${sv.price}€` : ""}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Checkouts del barco (checklist obligatoria antes de finalizar) */}
        {emb?.checkouts?.length > 0 && (
          <div className="sns-card p-5" data-testid="checklist-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="sns-title text-base flex items-center gap-2">
                <CheckSquare size={18} /> {t("detalle.checklist.title")} ({checklistDone}/{checklistTotal})
              </h3>
              {checklistComplete ? (
                <span className="sns-pill st-finalizado">{t("detalle.checklist.completed")}</span>
              ) : m.status !== "finalizado" && (
                <span className="sns-pill bg-amber-50 text-amber-700 border border-amber-200">{t("detalle.checklist.required")}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {emb.checkouts.map((z, i) => {
                const checked = !!checklistState[i];
                const lockedDone = m.status === "finalizado";
                return (
                  <label
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-xl transition-colors ${
                      lockedDone ? "opacity-70" : "hover:bg-slate-50 cursor-pointer"
                    } ${checked ? "bg-emerald-50" : ""}`}
                    data-testid={`checkout-${i}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => !lockedDone && toggleChecklist(i, !checked)}
                      disabled={lockedDone}
                      className="w-5 h-5 accent-[var(--coral)]"
                    />
                    <span className={`text-sm ${checked ? "text-emerald-700 font-semibold" : ""}`}>{z}</span>
                  </label>
                );
              })}
            </div>
            {!checklistComplete && m.status !== "finalizado" && (
              <p className="text-xs text-amber-700 mt-3">
                {t("detalle.checklist.hint")}
              </p>
            )}
          </div>
        )}

        {/* Fotos con comentarios */}
        <div className="sns-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="sns-title text-base">{t("detalle.photos.title")} ({m.photos?.length || 0})</h3>
            {m.status !== "finalizado" && (
              <button onClick={async () => {
                try {
                  const { isNative } = await import("@/lib/native");
                  if (isNative()) { await takeNativePhoto(); return; }
                } catch {}
                fileRef.current?.click();
              }} className="sns-btn sns-btn-accent py-2 px-3 text-sm" data-testid="btn-add-photo">
                <Camera size={14} /> {t("detalle.photos.add")}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
          </div>
          {m.photos?.length > 0 ? (
            <div className="space-y-3">
              {m.photos.map((p, i) => {
                const url = typeof p === "string" ? p : p.url;
                const comment = typeof p === "string" ? "" : (p.comment || "");
                return (
                  <div key={i} className="flex gap-3 items-start" data-testid={`photo-${i}`}>
                    <img src={url} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      {comment ? <p className="text-sm text-slate-700 leading-snug">{comment}</p> : <p className="text-xs text-slate-400 italic">{t("detalle.photos.no_comment")}</p>}
                    </div>
                    {m.status !== "finalizado" && (
                      <button onClick={() => deletePhoto(i)} className="text-red-500 text-xs font-semibold">{t("common.delete")}</button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : <p className="text-slate-400 text-sm">{t("detalle.photos.empty")}</p>}
        </div>

        {/* Incidencias — editable por admin y empleado asignado */}
        <div className="sns-card p-5" data-testid="incidencias-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="sns-title text-base flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" /> {t("detalle.incidents.title")}
            </h3>
            {!editInc && m.status !== "finalizado" && (
              <button
                onClick={() => { setIncText(m.incidencias || ""); setEditInc(true); }}
                className="text-sm text-[var(--coral)] font-semibold"
                data-testid="btn-edit-incidencias"
              >
                {m.incidencias ? t("detalle.edit") : t("detalle.add")}
              </button>
            )}
          </div>
          {editInc ? (
            <div className="space-y-2">
              <textarea
                value={incText}
                onChange={(e) => setIncText(e.target.value)}
                rows={4}
                placeholder={t("detalle.incidents.placeholder")}
                className="sns-input resize-none"
                data-testid="incidencias-textarea"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { setEditInc(false); setIncText(""); }}
                  className="sns-btn sns-btn-ghost bg-slate-100"
                  data-testid="btn-cancel-incidencias"
                >
                  <X size={16} /> {t("common.cancel")}
                </button>
                <button
                  onClick={saveIncidencias}
                  disabled={savingInc}
                  className="sns-btn sns-btn-accent disabled:opacity-60"
                  data-testid="btn-save-incidencias"
                >
                  <Save size={16} /> {savingInc ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </div>
          ) : m.incidencias ? (
            <p className="text-slate-700 whitespace-pre-wrap" data-testid="incidencias-text">{m.incidencias}</p>
          ) : (
            <p className="text-slate-400 text-sm italic">{t("detalle.incidents.empty")}</p>
          )}
        </div>

        <div className="space-y-2 pt-2">
          {myStatus === "programado" && (
            <button onClick={doCheckin} disabled={busy} className="sns-btn sns-btn-accent w-full py-4 text-base" data-testid="btn-checkin">
              <Play size={18} /> {t("detalle.btn.start")}
            </button>
          )}
          {myStatus === "en_curso" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={doPause} disabled={busy} className="sns-btn bg-amber-500 text-white hover:bg-amber-600 py-4 text-base" data-testid="btn-pause">
                  <Pause size={18} /> {t("detalle.btn.pause")}
                </button>
                <button
                  onClick={doCheckout}
                  disabled={busy || !canFinish}
                  className="sns-btn sns-btn-accent py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-checkout"
                  title={!canFinish ? finishHint : ""}
                >
                  <Square size={18} /> {t("detalle.btn.finish")}
                </button>
              </div>
              {!canFinish && (
                <p className="text-xs text-amber-700 text-center mt-1" data-testid="finish-blocked-hint">{finishHint}</p>
              )}
            </>
          )}
          {myStatus === "pausado" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={doResume} disabled={busy} className="sns-btn bg-emerald-600 text-white hover:bg-emerald-700 py-4 text-base" data-testid="btn-resume">
                  <Play size={18} /> {t("detalle.btn.resume")}
                </button>
                <button
                  onClick={doCheckout}
                  disabled={busy || !canFinish}
                  className="sns-btn sns-btn-accent py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-checkout-paused"
                  title={!canFinish ? finishHint : ""}
                >
                  <Square size={18} /> {t("detalle.btn.finish")}
                </button>
              </div>
              {!canFinish && (
                <p className="text-xs text-amber-700 text-center mt-1" data-testid="finish-blocked-hint">{finishHint}</p>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <button
                onClick={async () => {
                  setBusy(true); setErr("");
                  try {
                    const { data } = await api.post(`/mantenimientos/${id}/holded-presupuesto`);
                    alert(t("detalle.btn.holded.ok", { id: data.holded_id || "—" }));
                  } catch (ex) {
                    const e = fmtErr(ex);
                    alert(e.includes("Falta") || e.includes("HOLDED_API_KEY")
                      ? t("detalle.btn.holded.no_key")
                      : `${t("common.error")}: ${e}`);
                  } finally { setBusy(false); }
                }}
                disabled={busy}
                className="sns-btn bg-white border border-[var(--coral)] text-[var(--coral)] hover:bg-orange-50 w-full"
                data-testid="btn-emit-holded"
              >
                {t("detalle.btn.emit_holded")}
              </button>
              <button onClick={remove} className="sns-btn w-full text-red-600 bg-red-50 hover:bg-red-100" data-testid="btn-delete-job">
                <Trash2 size={16} /> {t("detalle.btn.delete")}
              </button>
            </>
          )}
        </div>
      </div>

      {pendingPhoto && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4"
          onClick={() => { if (!uploadingPhoto) { setPendingPhoto(null); setPhotoComment(""); } }}
        >
          <div className="bg-white rounded-3xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="sns-title text-xl mb-3">{t("detalle.photos.new")}</h3>
            <img src={pendingPhoto} alt="preview" className="w-full rounded-2xl mb-3 max-h-64 object-cover" />
            <label className="block text-xs font-semibold text-slate-600 mb-1">{t("detalle.photos.comment")}</label>
            <textarea value={photoComment} onChange={(e) => setPhotoComment(e.target.value)} rows={3}
                      className="sns-input resize-none mb-3"
                      placeholder={t("detalle.photos.comment.placeholder")}
                      disabled={uploadingPhoto}
                      data-testid="photo-comment-input" />
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setPendingPhoto(null); setPhotoComment(""); }}
                disabled={uploadingPhoto}
                className="sns-btn sns-btn-ghost bg-slate-100 disabled:opacity-50"
              >{t("common.cancel")}</button>
              <button
                onClick={savePhoto}
                disabled={uploadingPhoto}
                className="sns-btn sns-btn-accent disabled:opacity-60"
                data-testid="btn-save-photo"
              >
                {uploadingPhoto ? t("common.uploading") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex items-center gap-2 text-slate-500">{icon}<span>{label}</span></div>
    <div className="font-semibold text-right truncate">{value}</div>
  </div>
);

// Tarjeta de horas reales: muestra checkin/checkout + horas trabajadas + horas en pausa.
// Admin puede editarlo siempre (para correcciones manuales).
function HorasRealesCard({ m, isAdmin, onUpdated, setErr, t }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  function toLocalInput(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  }

  // Calcula horas reales desde checkin/checkout si no hay hours_worked guardado todavía
  const computedHours = (() => {
    if (m.hours_worked != null && m.hours_worked > 0) return m.hours_worked;
    if (m.accumulated_seconds && m.accumulated_seconds > 0) return Math.round(m.accumulated_seconds / 36) / 100;
    if (m.checkin_at && m.checkout_at) {
      try {
        const ms = new Date(m.checkout_at) - new Date(m.checkin_at);
        const pausedMs = (m.paused_seconds || 0) * 1000;
        return Math.max(0, Math.round((ms - pausedMs) / 36000) / 100);
      } catch { return 0; }
    }
    return 0;
  })();
  const computedPaused = Math.round(((m.paused_seconds || 0) / 36)) / 100;

  // Form state: se inicializa al ENTRAR en modo edición (no antes), así siempre muestra el valor actual
  const [horas, setHoras] = useState("");
  const [pausa, setPausa] = useState("");
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");

  const startEdit = () => {
    setHoras(String(computedHours || 0));
    setPausa(String(computedPaused || 0));
    setCheckin(toLocalInput(m.checkin_at));
    setCheckout(toLocalInput(m.checkout_at));
    setEditing(true);
  };

  const fmtT = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  const fmtH = (h) => {
    if (!h || h <= 0) return "—";
    const hh = Math.floor(h);
    const mm = Math.round((h - hh) * 60);
    if (hh === 0) return `${mm} min`;
    if (mm === 0) return `${hh} h`;
    return `${hh} h ${mm} min`;
  };

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const body = {};
      const h = parseFloat(horas);
      const p = parseFloat(pausa);
      if (!isNaN(h) && h >= 0) body.hours_worked = h;
      if (!isNaN(p) && p >= 0) body.paused_hours = p;
      if (checkin) body.checkin_at = new Date(checkin).toISOString();
      if (checkout) body.checkout_at = new Date(checkout).toISOString();
      const { data } = await api.patch(`/mantenimientos/${m.id}/horas-reales`, body);
      onUpdated(data);
      setEditing(false);
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };

  return (
    <div className="sns-card p-5 space-y-3 text-sm" data-testid="horas-reales-card">
      <div className="flex items-center justify-between">
        <h3 className="sns-title text-base flex items-center gap-2">
          <Clock size={18} /> {t("detalle.real_hours")}
        </h3>
        {isAdmin && !editing && (
          <button
            onClick={startEdit}
            className="text-sm text-[var(--coral)] font-semibold"
            data-testid="btn-edit-horas-reales"
          >
            {t("detalle.edit")}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("detalle.real_start")}</label>
              <input type="datetime-local" value={checkin} onChange={(e) => setCheckin(e.target.value)} className="sns-input" data-testid="input-checkin" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("detalle.real_end")}</label>
              <input type="datetime-local" value={checkout} onChange={(e) => setCheckout(e.target.value)} className="sns-input" data-testid="input-checkout" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("detalle.hours_worked")}</label>
              <input type="number" step="0.01" min="0" value={horas} onChange={(e) => setHoras(e.target.value)} className="sns-input" data-testid="input-horas-trabajadas" />
              <p className="text-[10px] text-slate-400 mt-0.5">{t("detalle.real_hours.input_hint")}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("detalle.pause_hours")}</label>
              <input type="number" step="0.01" min="0" value={pausa} onChange={(e) => setPausa(e.target.value)} className="sns-input" data-testid="input-horas-pausa" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setEditing(false)} className="sns-btn sns-btn-ghost bg-slate-100" disabled={saving}>{t("common.cancel")}</button>
            <button onClick={submit} className="sns-btn sns-btn-accent disabled:opacity-50" disabled={saving} data-testid="btn-save-horas-reales">
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
          <p className="text-xs text-slate-400">{t("detalle.real_hours.hint")}</p>
        </div>
      ) : (
        <>
          <Row icon={<Clock size={16} />} label={t("detalle.real_start")} value={fmtT(m.checkin_at)} />
          <Row icon={<Clock size={16} />} label={t("detalle.real_end")} value={fmtT(m.checkout_at)} />
          <Row icon={<Clock size={16} />} label={t("detalle.worked_label")} value={fmtH(computedHours)} />
          {computedPaused > 0 && <Row icon={<Pause size={16} />} label={t("detalle.pause_label")} value={fmtH(computedPaused)} />}
          {m.checkin_at && m.checkout_at == null && (
            <p className="text-xs text-amber-700">{t("detalle.in_progress_warn")}</p>
          )}
        </>
      )}
    </div>
  );
}

function SignatureModal({ onClose, onSave }) {
  const canvasRef = useRef();
  const [name, setName] = useState("");
  const drawing = useRef(false);
  const getCtx = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0B1220"; return ctx; };
  const getPos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const t = e.touches?.[0]; const cx = t ? t.clientX : e.clientX; const cy = t ? t.clientY : e.clientY; return { x: cx - r.left, y: cy - r.top }; };
  const start = (e) => { drawing.current = true; const p = getPos(e); const ctx = getCtx(); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = getPos(e); const ctx = getCtx(); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const save = () => onSave(canvasRef.current.toDataURL("image/png"), name);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="sns-title text-xl mb-3">Firma del cliente</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del firmante" className="sns-input mb-3" data-testid="signature-name" />
        <div className="border-2 border-dashed border-slate-200 rounded-2xl overflow-hidden bg-slate-50 mb-3">
          <canvas ref={canvasRef} width={400} height={220} className="w-full touch-none"
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={clear} className="sns-btn sns-btn-ghost bg-slate-100">Limpiar</button>
          <button onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100">Cancelar</button>
          <button onClick={save} className="sns-btn sns-btn-accent" data-testid="signature-save">Guardar</button>
        </div>
      </div>
    </div>
  );
}
